import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Chip, Grid, TextField, IconButton, Dialog,
  Tooltip, Select, MenuItem, InputAdornment, LinearProgress,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface Product {
  id: number; productName: string; productBarcode: string; productCategoryId: number;
  productPrice: number; salePrice: number; wholesaleprice: number;
  current_stock: number; minimum_stock: number; reorder_point: number; reorder_qty: number;
  track_inventory: boolean; qty: number; stock_status: string; needs_reorder: boolean;
}
interface Summary { total: number; tracked: number; good: number; low: number; critical: number; out_of_stock: number; needs_reorder: number; }
interface Movement { id: number; product_id: number; type: string; qty: number; stock_before: number; stock_after: number; reference: string; notes: string; created_at: string; product?: { id: number; productName: string }; }

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  good: { label: "Good", color: "#16a34a", bg: "#f0fdf4", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  low: { label: "Low", color: "#ea580c", bg: "#fff7ed", icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
  critical: { label: "Critical", color: "#dc2626", bg: "#fef2f2", icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  out: { label: "Out", color: "#7f1d1d", bg: "#fef2f2", icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  untracked: { label: "Untracked", color: "#94a3b8", bg: "#f8fafc", icon: <InventoryIcon sx={{ fontSize: 14 }} /> },
};

const thSx = { fontWeight: 600, fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fafbfc" };

const Warehouse: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState({ current_stock: 0, minimum_stock: 0, reorder_point: 0, reorder_qty: 0, track_inventory: true });
  const [saving, setSaving] = useState(false);
  const [adjustProduct, setAdjustProduct] = useState<Product | null>(null);
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustType, setAdjustType] = useState<"add" | "deduct">("add");
  const [adjustNotes, setAdjustNotes] = useState("");
  const [movements, setMovements] = useState<Movement[]>([]);
  const [movementProduct, setMovementProduct] = useState<Product | null>(null);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/dashboard`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ search, status: statusFilter, track_only: false }),
      });
      const json = await res.json();
      if (json.success) { setProducts(json.data || []); setSummary(json.summary || null); }
    } catch {} finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async () => {
    if (!editProduct) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/product/${editProduct.id}/settings`, {
        method: "PUT", headers: getHeaders(), body: JSON.stringify(editForm),
      });
      const json = await res.json();
      if (json.success) { setEditProduct(null); load(); }
    } catch {} finally { setSaving(false); }
  };

  const handleAdjust = async () => {
    if (!adjustProduct || !adjustQty) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/product/${adjustProduct.id}/adjust`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ qty: Number(adjustQty), type: adjustType, notes: adjustNotes }),
      });
      const json = await res.json();
      if (json.success) { setAdjustProduct(null); setAdjustQty(""); setAdjustNotes(""); load(); }
    } catch {} finally { setSaving(false); }
  };

  const loadMovements = async (product: Product) => {
    setMovementProduct(product); setMovementsLoading(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/movements/${product.id}`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) setMovements(json.data || []);
    } catch {} finally { setMovementsLoading(false); }
  };

  const openEdit = (p: Product) => {
    setEditForm({ current_stock: p.current_stock, minimum_stock: p.minimum_stock, reorder_point: p.reorder_point, reorder_qty: p.reorder_qty, track_inventory: p.track_inventory });
    setEditProduct(p);
  };

  const stockPct = (p: Product) => {
    if (!p.track_inventory || p.reorder_point <= 0) return 100;
    return Math.min(100, Math.round((p.current_stock / (p.reorder_point * 2)) * 100));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: "auto", minHeight: "100vh", bgcolor: "#f8f9fb" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 3, background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(30,41,59,0.25)" }}>
          <InventoryIcon sx={{ fontSize: 24, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={26} fontWeight={800} color="#0f172a" letterSpacing="-0.03em">Warehouse Inventory</Typography>
          <Typography fontSize={13} color="#94a3b8">Monitor stock levels, adjust quantities, and manage reorder points</Typography>
        </Box>
        <Button variant="contained" disableElevation startIcon={<RefreshIcon />} onClick={load}
          sx={{ borderRadius: 2.5, py: 1, px: 3, fontWeight: 700, fontSize: 13, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none" }}>
          Refresh
        </Button>
      </Box>

      {/* Summary Cards — clickable filters */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total Products", value: summary.total, filter: "all", color: "#3b82f6", bg: "#eff6ff", gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)", border: "#93c5fd" },
            { label: "Tracked", value: summary.tracked, filter: "tracked", color: "#8b5cf6", bg: "#faf5ff", gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)", border: "#c4b5fd" },
            { label: "Good Stock", value: summary.good, filter: "good", color: "#16a34a", bg: "#f0fdf4", gradient: "linear-gradient(135deg, #16a34a, #15803d)", border: "#86efac" },
            { label: "Low Stock", value: summary.low, filter: "low", color: "#ea580c", bg: "#fff7ed", gradient: "linear-gradient(135deg, #ea580c, #c2410c)", border: "#fdba74" },
            { label: "Critical", value: summary.critical, filter: "critical", color: "#dc2626", bg: "#fef2f2", gradient: "linear-gradient(135deg, #dc2626, #b91c1c)", border: "#fca5a5" },
            { label: "Needs Reorder", value: summary.needs_reorder, filter: "reorder", color: "#7c3aed", bg: "#faf5ff", gradient: "linear-gradient(135deg, #7c3aed, #6d28d9)", border: "#c4b5fd" },
          ].map((c) => {
            const isActive = statusFilter === c.filter || (statusFilter === "all" && c.filter === "all");
            return (
              <Grid item xs={6} sm={2} key={c.filter}>
                <Paper elevation={0} onClick={() => setStatusFilter(c.filter === statusFilter ? "all" : c.filter)}
                  sx={{
                    p: 2, borderRadius: 3, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
                    transition: "all 0.2s ease",
                    border: isActive && c.filter !== "all" ? `2px solid ${c.border}` : "1px solid rgba(0,0,0,0.06)",
                    bgcolor: isActive && c.filter !== "all" ? c.bg : "#fff",
                    boxShadow: isActive && c.filter !== "all" ? `0 4px 14px ${c.color}20` : "none",
                    transform: isActive && c.filter !== "all" ? "translateY(-2px)" : "none",
                    "&:hover": { boxShadow: `0 4px 12px ${c.color}15`, transform: "translateY(-1px)" },
                  }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: c.gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Typography fontSize={16} fontWeight={800} color="#fff">{c.value}</Typography>
                  </Box>
                  <Typography fontSize={11} color={isActive && c.filter !== "all" ? c.color : "#64748b"} fontWeight={isActive ? 700 : 600} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{c.label}</Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Search + Active Filter */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <TextField size="small" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
          sx={{ minWidth: 250, "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fafbfc" } }} />
        {statusFilter !== "all" && (() => {
          const filterLabels: Record<string, { label: string; color: string; bg: string }> = {
            good: { label: "Good Stock", color: "#16a34a", bg: "#f0fdf4" },
            low: { label: "Low Stock", color: "#ea580c", bg: "#fff7ed" },
            critical: { label: "Critical", color: "#dc2626", bg: "#fef2f2" },
            out: { label: "Out of Stock", color: "#7f1d1d", bg: "#fef2f2" },
            reorder: { label: "Needs Reorder", color: "#7c3aed", bg: "#faf5ff" },
            tracked: { label: "Tracked Only", color: "#8b5cf6", bg: "#faf5ff" },
            untracked: { label: "Untracked", color: "#94a3b8", bg: "#f8fafc" },
          };
          const fl = filterLabels[statusFilter] || { label: statusFilter, color: "#64748b", bg: "#f8fafc" };
          return (
            <Chip label={`Showing: ${fl.label}`} size="small" onDelete={() => setStatusFilter("all")}
              sx={{ height: 28, fontSize: 11, fontWeight: 700, bgcolor: fl.bg, color: fl.color,
                "& .MuiChip-deleteIcon": { color: fl.color, fontSize: 16, "&:hover": { color: fl.color } } }} />
          );
        })()}
        {statusFilter === "all" && (
          <Typography fontSize={12} color="#94a3b8">Click a summary card above to filter</Typography>
        )}
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress size={28} sx={{ color: "#00838f" }} /></Box>
        ) : (
          <TableContainer sx={{ maxHeight: "calc(100vh - 340px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Product</TableCell>
                  <TableCell sx={thSx} align="center">Status</TableCell>
                  <TableCell sx={thSx} align="right">Current Stock</TableCell>
                  <TableCell sx={thSx} align="right">Min Stock</TableCell>
                  <TableCell sx={thSx} align="right">Reorder Point</TableCell>
                  <TableCell sx={thSx} align="right">Reorder Qty</TableCell>
                  <TableCell sx={thSx} align="center">Stock Level</TableCell>
                  <TableCell sx={thSx} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map(p => {
                  const sc = statusConfig[p.stock_status] || statusConfig.untracked;
                  const pct = stockPct(p);
                  const barColor = pct >= 60 ? "#16a34a" : pct >= 30 ? "#ea580c" : "#dc2626";
                  return (
                    <TableRow key={p.id} sx={{ "&:hover": { bgcolor: "#fafbfe" }, bgcolor: p.needs_reorder ? "#fffbeb" : "inherit" }}>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize={13} fontWeight={600} color="#1e293b" sx={{ textTransform: "capitalize" }}>{p.productName}</Typography>
                        {p.productBarcode && <Typography fontSize={10} color="#94a3b8">{p.productBarcode}</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25 }}>
                        <Chip icon={sc.icon as React.ReactElement} label={sc.label} size="small"
                          sx={{ height: 24, fontSize: 10, fontWeight: 700, bgcolor: sc.bg, color: sc.color, "& .MuiChip-icon": { color: sc.color } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={16} fontWeight={800} color={p.current_stock <= 0 ? "#dc2626" : "#0f172a"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {p.track_inventory ? p.current_stock : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.track_inventory ? p.minimum_stock : "—"}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.track_inventory ? p.reorder_point : "—"}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.track_inventory ? p.reorder_qty : "—"}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25, width: 120 }}>
                        {p.track_inventory ? (
                          <Tooltip title={`${pct}% of safe level`}>
                            <Box>
                              <LinearProgress variant="determinate" value={pct}
                                sx={{ height: 6, borderRadius: 3, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 } }} />
                              <Typography fontSize={9} color="#94a3b8" mt={0.25}>{pct}%</Typography>
                            </Box>
                          </Tooltip>
                        ) : <Typography fontSize={10} color="#cbd5e1">N/A</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25 }}>
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <Tooltip title="Edit Settings"><IconButton size="small" onClick={() => openEdit(p)} sx={{ width: 28, height: 28 }}><EditIcon sx={{ fontSize: 14, color: "#64748b" }} /></IconButton></Tooltip>
                          <Tooltip title="Adjust Stock"><IconButton size="small" onClick={() => { setAdjustProduct(p); setAdjustType("add"); setAdjustQty(""); setAdjustNotes(""); }} sx={{ width: 28, height: 28 }}><AddIcon sx={{ fontSize: 14, color: "#16a34a" }} /></IconButton></Tooltip>
                          <Tooltip title="Stock History"><IconButton size="small" onClick={() => loadMovements(p)} sx={{ width: 28, height: 28 }}><HistoryIcon sx={{ fontSize: 14, color: "#3b82f6" }} /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {products.length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 8 }}>
                    <Typography fontSize={14} color="#94a3b8">No products found</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Settings Dialog */}
      <Dialog open={!!editProduct} onClose={() => setEditProduct(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">Inventory Settings</Typography>
            <IconButton size="small" onClick={() => setEditProduct(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {editProduct && <Typography fontSize={13} color="#64748b" mb={2} sx={{ textTransform: "capitalize" }}>{editProduct.productName}</Typography>}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography fontSize={12} fontWeight={600} color="#64748b">Track Inventory</Typography>
              <Chip label={editForm.track_inventory ? "ON" : "OFF"} size="small"
                onClick={() => setEditForm(f => ({ ...f, track_inventory: !f.track_inventory }))}
                sx={{ height: 24, fontSize: 11, fontWeight: 700, cursor: "pointer", bgcolor: editForm.track_inventory ? "#f0fdf4" : "#f8fafc", color: editForm.track_inventory ? "#16a34a" : "#94a3b8" }} />
            </Box>
            {[
              { key: "current_stock", label: "Current Stock" },
              { key: "minimum_stock", label: "Minimum Stock" },
              { key: "reorder_point", label: "Reorder Point" },
              { key: "reorder_qty", label: "Reorder Quantity" },
            ].map(f => (
              <TextField key={f.key} size="small" label={f.label} type="number"
                value={(editForm as any)[f.key]} onChange={e => setEditForm(prev => ({ ...prev, [f.key]: Number(e.target.value) }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            ))}
            <Button variant="contained" disableElevation onClick={handleSaveSettings} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 2.5, py: 1, fontWeight: 700, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none" }}>
              Save Settings
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!adjustProduct} onClose={() => setAdjustProduct(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">Adjust Stock</Typography>
            <IconButton size="small" onClick={() => setAdjustProduct(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {adjustProduct && (
            <>
              <Typography fontSize={13} color="#64748b" mb={0.5} sx={{ textTransform: "capitalize" }}>{adjustProduct.productName}</Typography>
              <Typography fontSize={12} color="#94a3b8" mb={2}>Current: <strong>{adjustProduct.current_stock}</strong></Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip label="Add Stock" size="small" onClick={() => setAdjustType("add")}
                  sx={{ cursor: "pointer", fontWeight: 600, bgcolor: adjustType === "add" ? "#f0fdf4" : "#f8fafc", color: adjustType === "add" ? "#16a34a" : "#94a3b8", border: `1px solid ${adjustType === "add" ? "#bbf7d0" : "rgba(0,0,0,0.06)"}` }} />
                <Chip label="Deduct Stock" size="small" onClick={() => setAdjustType("deduct")}
                  sx={{ cursor: "pointer", fontWeight: 600, bgcolor: adjustType === "deduct" ? "#fef2f2" : "#f8fafc", color: adjustType === "deduct" ? "#dc2626" : "#94a3b8", border: `1px solid ${adjustType === "deduct" ? "#fecaca" : "rgba(0,0,0,0.06)"}` }} />
              </Box>
              <TextField size="small" fullWidth label="Quantity" type="number" value={adjustQty} onChange={e => setAdjustQty(e.target.value)}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <TextField size="small" fullWidth label="Notes (optional)" value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <Button variant="contained" disableElevation fullWidth onClick={handleAdjust} disabled={saving || !adjustQty}
                sx={{ borderRadius: 2.5, py: 1, fontWeight: 700, bgcolor: adjustType === "add" ? "#16a34a" : "#dc2626", "&:hover": { bgcolor: adjustType === "add" ? "#15803d" : "#b91c1c" }, textTransform: "none" }}>
                {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : `${adjustType === "add" ? "Add" : "Deduct"} ${adjustQty || 0} units`}
              </Button>
            </>
          )}
        </Box>
      </Dialog>

      {/* Movements Dialog */}
      <Dialog open={!!movementProduct} onClose={() => setMovementProduct(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">Stock History</Typography>
            <IconButton size="small" onClick={() => setMovementProduct(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {movementProduct && <Typography fontSize={13} color="#64748b" mb={2} sx={{ textTransform: "capitalize" }}>{movementProduct.productName}</Typography>}
          {movementsLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></Box>
          ) : movements.length === 0 ? (
            <Typography fontSize={13} color="#94a3b8" textAlign="center" py={4}>No stock movements recorded</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Date</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Type</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }} align="right">Qty</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }} align="right">After</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map(m => (
                    <TableRow key={m.id}>
                      <TableCell sx={{ fontSize: 11, py: 0.75 }}>{new Date(m.created_at).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip label={m.type.replace("_", " ")} size="small"
                          sx={{ height: 20, fontSize: 9, fontWeight: 700, textTransform: "capitalize",
                            bgcolor: m.qty > 0 ? "#f0fdf4" : "#fef2f2", color: m.qty > 0 ? "#16a34a" : "#dc2626" }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75 }}>
                        <Typography fontSize={12} fontWeight={700} color={m.qty > 0 ? "#16a34a" : "#dc2626"}>
                          {m.qty > 0 ? "+" : ""}{m.qty}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, py: 0.75, fontWeight: 600 }}>{m.stock_after}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: "#94a3b8", py: 0.75 }}>{m.reference || m.notes || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default Warehouse;
